<template>
  <div class="fill-in-blank-view">
    <cv-form>
      <dl v-if="isSharedDropdown" class="field-set type-text helping-words">
        <dd class="field-value">
          <AccordionWrapper>
            <cv-accordion-item :open="true">
              <template #title>
                <span class="title">
                  {{ t("fitb_option_accordion_title") }}
                </span>
              </template>
              <template #content>
                <ul class="helping-word-list">
                  <li v-for="(word, index) in helpingWords" :key="index" class="helping-word-item">
                    <span>{{ word }}</span>
                  </li>
                </ul>
              </template>
            </cv-accordion-item>
          </AccordionWrapper>
        </dd>
      </dl>

      <!-- Question Body -->
      <match-indicator v-if="props.highlights" />
      <dl ref="container" class="field-set type-text question-body">
        <dd class="field-value">
          <rich-text-wrapper
            ref="richTextRef"
            :content="questionBody"
            :expandable="true"
            :expanded="true"
            :can-annotate="canAccessAnnotations"
            :anchor="questionBodyAnchor"
            :resource-type="'LESSON_COMPONENT'"
            :annotation-resource-id="questionAnnotationResourceId"
            @rich-text-ready="onRichTextReady"
          />
        </dd>
      </dl>

      <!-- Hint -->
      <dl v-if="questionHints && questionHints.length" class="field-set type-text hint">
        <dd class="field-value">
          <AccordionWrapper v-for="(hint, index) in questionHints" :key="hintId(hint, index)">
            <cv-accordion-item :open="true">
              <template #title>
                <span class="icon">
                  <svg-icon name="Hint16" kind="indicative" />
                </span>
                <span class="title">{{ hintTitle(index) }}</span>
              </template>
              <template #content>
                <rich-text class="hint" :content="hint.description" :expandable="false" />
              </template>
            </cv-accordion-item>
          </AccordionWrapper>
        </dd>
      </dl>

      <!-- Case Sensitive Message -->
      <dl v-if="showCaseSensitiveMessage" class="field-set type-text case-sensitive">
        <dd class="field-value">
          {{ t("question_component_label_all_answers_case_sensitive") }}
        </dd>
      </dl>

      <!-- Fill Answers -->
      <dl
        v-for="(row, $index) in answerDisplayRows"
        :key="row.id"
        class="field-set type-text answer-options"
      >
        <dd class="field-value">
          <fill-in-blank-answer-view
            :answer="row.option"
            :group-color="row.color"
            :index="row.blankNumbers[0] ?? $index + 1"
            :is-grouped="row.isGrouped"
            :label="answerDisplayLabel(row, $index)"
            :member-blank-numbers="row.blankNumbers"
            :option-display-mode="props.question.optionDisplayMode"
            :show-case-sensitive="isTextInput"
          />
        </dd>
      </dl>

      <!-- Distractor -->
      <div v-if="distractors" class="field-group distractors option-list">
        <dl class="field-set option-set">
          <dd class="field-value">
            <span class="icon">
              <svg-icon name="Cross16" kind="indicative" />
            </span>
            <div class="label">{{ t("question_component_label_distractors_title") }}</div>
          </dd>
        </dl>
        <dl class="field-set distractor-list">
          <dt class="field-label">
            <h5 class="title">{{ t("question_component_label_distractors_title") }}</h5>
            <cv-button
              v-if="isDistractorsExpandable"
              class="view-all"
              kind="tertiary"
              size="small"
              type="button"
              @click.stop="handleViewAllDistractors"
              >{{ t("question_component_label_view_all") }}
              <span class="number">({{ distractorList.length }})</span>
            </cv-button>
          </dt>
          <dd class="field-value">
            <div
              v-for="(distractor, index) in minimalDistractorList"
              :key="index"
              class="distractor option"
            >
              {{ distractor }}
            </div>
          </dd>
        </dl>
      </div>

      <!-- Feedback(Explanation) -->
      <dl v-if="feedback" class="field-set type-text feedback">
        <dd class="field-value">
          <rich-text-card title="Feedback" :content="feedback" truncation-type="secondary">
            <template #icon><svg-icon name="Feedback32" kind="indicative" /></template>
          </rich-text-card>
        </dd>
      </dl>
    </cv-form>
  </div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import { useContentSubpageInstance } from "@plugins/content-subpage/composables/content-subpage-instance";
import { useAnnotationPermissions } from "@modules/annotation/composables/use-annotation-permissions.ts";
import { useQuestionAnnotation } from "@modules/editor/composables/use-question-annotation.ts";
import FillInBlankAnswerView from "@modules/editor/components/question/FillInBlankAnswerView.vue";
import {
  ALPHABETS_LIST,
  EDITOR_MODE,
  OPTION_DISPLAY_MODE,
} from "@modules/editor/service/editor.constant";
import type { FillInBlankGroupDisplayRow } from "@modules/editor/service/fill-in-blank-grouping.utils";
import {
  getGroupColor,
  getGroupDisplayRows,
} from "@modules/editor/service/fill-in-blank-grouping.utils";
import type { FillInBlankOptionDraft } from "@modules/editor/service/fill-in-blank.utils";
import BlankUtils from "@plugins/richtext/utils/blank.utils";
import RichTextWrapper from "@/plugins/richtext/components/RichTextWrapper.vue";
import { cloneDeep } from "lodash";

defineOptions({
  name: "FillInBlankView",
});

interface QuestionHint {
  id?: string;
  description: string;
}

interface QuestionOption {
  id?: number | string;
  answerOption: boolean;
  answer: string;
  caseSensitive?: boolean;
  feedback?: string;
  optionGroupNo?: number | null;
  wrongAnswer?: string;
  wrongAnswerFeedback?: string;
}

interface Question {
  questionBody: string;
  explanation?: string;
  questionOptionModels: QuestionOption[];
  questionHintModels?: QuestionHint[];
  optionDisplayMode: string;
  uniqueOption?: boolean;
  uuid: string;
}

interface Props {
  question: Question;
  highlights?: object;
}

const props = withDefaults(defineProps<Props>(), {
  highlights: undefined,
});

const emit = defineEmits<{
  loaded: [uuid: string];
}>();

const { t } = useI18n();
const { canAccessAnnotations } = useAnnotationPermissions();
const contentSubpageInstance = useContentSubpageInstance();
const { questionBodyAnchor, questionAnnotationResourceId } = useQuestionAnnotation();

const splitOptionValues = (value?: string) => {
  if (!value) {
    return [];
  }

  return value.split("|").filter((item) => item.length > 0);
};

// Refs
const containerRef = useTemplateRef("container");
const richTextRef = useTemplateRef("richTextRef");

// Computed
const questionBody = computed(() => props.question.questionBody);

const feedback = computed(() => props.question.explanation);

const answerOptions = computed(() =>
  props.question.questionOptionModels.filter((option) => option.answerOption),
);

const answerDisplayRows = computed<FillInBlankGroupDisplayRow[]>(() => {
  return getGroupDisplayRows(answerOptions.value as FillInBlankOptionDraft[]);
});

const distractors = computed(() =>
  props.question.questionOptionModels.find((option) => !option.answerOption),
);

const distractorList = computed(() => {
  if (!distractors.value) return [];
  return distractors.value.answer.split("|");
});

const minimalDistractorList = computed(() => {
  const minimalList = cloneDeep(distractorList.value);
  minimalList.splice(2, distractorList.value.length - 2);
  return minimalList;
});

const allCaseSensitive = computed(() =>
  answerDisplayRows.value.every((answer) => answer.option.caseSensitive === true),
);

const showCaseSensitiveMessage = computed(() => allCaseSensitive.value && isTextInput.value);

const questionHints = computed(() => props.question.questionHintModels);

const hintId = computed(() => (hint: QuestionHint, index: number) => {
  return hint.id ? hint.id.toString() : index.toString();
});

const hintTitle = computed(() => (index: number) => {
  return `${t("question_component_label_hint")} ${index + 1}`;
});

const helpingWords = computed(() => {
  const words: string[] = [];
  answerDisplayRows.value.forEach((row) => {
    words.push(...splitOptionValues(row.option.answer));
  });
  words.push(...splitOptionValues(distractors.value?.answer));
  const sortedWords = words.sort();
  return props.question.uniqueOption ? [...new Set(sortedWords)] : sortedWords;
});

const answerDisplayLabel = computed(() => {
  return (row: FillInBlankGroupDisplayRow, index: number) => {
    if (!row.isGrouped) {
      return t("question_component_label_blank_title", {
        count: row.blankNumbers[0] ?? index + 1,
      });
    }

    const letter = ALPHABETS_LIST[(row.optionGroupNo ?? 1) - 1] ?? String(row.optionGroupNo ?? "");
    return t("question_component_fitb_group_label", { letter });
  };
});

const groupLabelByNumber = computed(() => {
  return (groupNo: number) => {
    const letter = ALPHABETS_LIST[groupNo - 1] ?? String(groupNo);
    return t("question_component_fitb_group_label", { letter });
  };
});

const groupMetadataByBlankId = computed(() => {
  return answerOptions.value.reduce<
    Record<string, { groupColor: string; groupLabel: string; groupNo: number }>
  >((metadata, option) => {
    if (option.optionGroupNo == null || option.id == null) {
      return metadata;
    }

    metadata[String(option.id)] = {
      groupColor: getGroupColor(option.optionGroupNo),
      groupLabel: groupLabelByNumber.value(option.optionGroupNo),
      groupNo: option.optionGroupNo,
    };

    return metadata;
  }, {});
});

const isTextInput = computed(
  () => props.question.optionDisplayMode === OPTION_DISPLAY_MODE.TEXT_INPUT.value,
);

const isSharedDropdown = computed(
  () => props.question.optionDisplayMode === OPTION_DISPLAY_MODE.SHARED_DROPDOWN.value,
);

const isDistractorsExpandable = computed(() => distractorList.value.length > 0);

// Methods
const handleViewAllDistractors = () => {
  const TagListContentSubpage = defineAsyncComponent(
    () => import("@plugins/common/components/TagListContentSubpage.vue"),
  );
  contentSubpageInstance.show(TagListContentSubpage, {
    title: t("question_component_label_distractors_title"),
    list: distractorList.value,
    listTitle: t("question_component_label_distractors_list_title"),
  });
};

const onRichTextReady = () => {
  BlankUtils.bindFillBlanksInContainer(
    containerRef.value,
    {
      useAnswerMap: false,
      answerMap: null,
      viewMode: EDITOR_MODE.VIEW,
      initialCorrect: true,
      disabled: true,
      groupMap: groupMetadataByBlankId.value,
      onChangeFn: null,
    },
    richTextRef.value,
  );
};

// Lifecycle
onMounted(() => {
  emit("loaded", props.question.uuid);
});
</script>

<style lang="scss">
@import "@styles/abstracts/functions";
@import "@styles/abstracts/mixins";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.fill-in-blank-view {
  .field-set {
    &.question-body {
      .field-value {
        @include carbon--type-style("question-02", true);
      }

      .rich-text-actions {
        margin-top: $spacing-02;
        line-height: 1rem;
      }
    }

    &.answer-options {
      border-bottom: 1px solid $ui-03;
    }

    &.feedback {
      margin-top: $spacing-05;

      .rich-text-card {
        width: 100%;
      }
    }
  }
  .edit & {
    .fill-in-blank-input {
      .answer {
        background-color: $ui-01;
      }
    }
  }

  .fill-in-blank-input {
    .answer {
      width: 240px;
    }
  }

  // Shared styling for answer view & distractors
  .field-group {
    &.option-list {
      .option-set {
        padding: 0;
        background-color: tint($state-positive, 90%);
        border-radius: 10px;
        color: $text-01;

        &.grouped {
          position: relative;
          border-radius: 10px;
          overflow: hidden;

          .field-value {
            // to align icon to center vertically when answer has multiple lines
            > .icon {
              top: 50%;
              transform: translateY(-50%);
            }
          }

          &::before {
            @include position-mixin($top: 0, $left: 0, $width: 4px, $height: 100%, $pseudo: true);
            background-color: var(--fitb-group-color);
          }
        }

        .option-feedback {
          border: 1px solid $ui-03;
          border-radius: 0 0 10px 10px;
        }

        .field-value {
          position: relative;
          width: 100%;
          margin-top: 0;

          > .icon {
            position: absolute;
            top: 1rem;
            left: 1rem;
            display: flex;
            justify-content: center;
            align-items: center;
            flex: 0 0 1.25rem;
            width: 1.25rem;
            height: 1.25rem;
            border-radius: 50%;
            border: 1px solid $state-positive;
            background-color: $icon-03;

            path {
              fill: $state-positive;
            }
          }
        }

        .label {
          display: flex;
          flex-direction: column;
          padding: $spacing-05 $spacing-09;

          .group-summary {
            @include carbon--type-style("label-02", true);
            margin-top: $spacing-01;
            color: $text-02;
          }
        }

        .case-sensitive.field-tooltip {
          align-self: center;
          margin-left: auto;
          margin-right: $spacing-05;

          .bx--tooltip__trigger {
            margin-left: 0;

            .sls-icon {
              [fill] {
                fill: $text-01;
              }
            }
          }
        }
      }

      .field-label {
        display: flex;
        align-items: center;
        margin: $spacing-05 0;

        .title {
          @include carbon--type-style("productive-heading-01", true);
          color: $text-01;
        }

        .view-all {
          margin-left: auto;

          .number {
            margin-left: $spacing-02;
          }
        }
      }
      .field-value {
        display: flex;

        .option {
          flex: 0 0 calc(50% - #{$spacing-03});
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          padding: $spacing-05;
          background-color: $ui-01;
          border: 1px solid $ui-03;
          border-radius: 10px;

          .edit & {
            background-color: $ui-01;
            border: none;
          }

          &:last-child {
            margin-left: $spacing-05;
          }

          &:only-child {
            flex: 0 0 100%;
            margin-left: 0;
          }
        }
      }
    }

    &.distractors {
      border-bottom: $divider-02;

      .option-set {
        background-color: tint($state-negative, 90%);

        .field-value {
          > .icon {
            background-color: $state-negative;
            border-color: $state-negative;
            path {
              fill: $icon-03;
            }
          }
        }
      }
    }
  }

  .helping-word-list {
    display: flex;
    flex-flow: row wrap;
    gap: $spacing-07;
    padding: $spacing-03 0;

    li {
      word-break: break-word;
    }
  }

  > .bx--form {
    > .feedback {
      margin-top: $spacing-04;
    }
  }

  .fill-in-blank-view {
    .field-set {
      &.answer-list {
        margin-bottom: $spacing-04;
      }
      .answer-option {
        margin-bottom: $spacing-04;
      }
    }
  }
}
</style>
